import React from 'react';
import { Building2, Home, Gift, CheckCircle2, AlertCircle, Briefcase } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { LEASE_RATES } from '../../data/leaseAndAids';

/**
 * Finance — onglet « Loyers & Aides ».
 * Affiche :
 *   - Détail du loyer mensuel (HQ / dépôts secondaires / dépôts M&A / bureau)
 *   - Aides régionales actives (avec date de fin)
 *   - Preview des aides éligibles au prochain 1er janvier
 *
 * 29/05/2026 — Phase A + B.
 */
const CATEGORY_DEFS = [
  { key: 'hq',        label: 'Dépôt HQ (principal)',          icon: Building2, rate: LEASE_RATES.hq },
  { key: 'secondary', label: 'Dépôts secondaires (construits)', icon: Building2, rate: LEASE_RATES.secondary },
  { key: 'acquired',  label: 'Dépôts M&A (acquis)',           icon: Building2, rate: LEASE_RATES.acquired },
  { key: 'office',    label: 'Bureau / Siège social',         icon: Briefcase, rate: LEASE_RATES.office },
];

export default function LeaseAidsTab() {
  const computeMonthlyRent = useGameStore(s => s.computeMonthlyRent);
  const previewYearStartAids = useGameStore(s => s.previewYearStartAids);
  const getActiveAids = useGameStore(s => s.getActiveAids);
  const totalDays = useGameStore(s => s.totalDays || 0);

  const rent = computeMonthlyRent ? computeMonthlyRent() : { gross: 0, net: 0, byCategory: { hq: 0, secondary: 0, acquired: 0, office: 0 }, counts: { hq: 1, secondary: 0, acquired: 0 }, officeUnlocked: false, aidsApplied: [] };
  const activeAids = getActiveAids ? getActiveAids() : [];
  const preview = previewYearStartAids ? previewYearStartAids() : [];
  const totalSaved = rent.gross - rent.net;

  return (
    <div className="space-y-4" data-testid="lease-aids-tab">
      {/* Loyer mensuel — breakdown */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="lease-breakdown">
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-center gap-2">
            <Home className="w-4 h-4 text-cyan-300" />
            <p className="text-sm font-semibold text-slate-200">Loyer mensuel — bâtiments</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500">Net après aides</p>
            <p className="text-lg font-bold text-amber-300">{formatMoney(rent.net)}<span className="text-xs text-slate-500"> /mois</span></p>
          </div>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 text-left">
              <th className="py-1.5 pr-3 font-normal">Catégorie</th>
              <th className="py-1.5 px-2 font-normal text-center">Qté</th>
              <th className="py-1.5 px-2 font-normal text-right">Tarif unitaire</th>
              <th className="py-1.5 pl-2 font-normal text-right">Sous-total</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_DEFS.map(cat => {
              let qty = 0;
              if (cat.key === 'hq') qty = rent.counts.hq || 0;
              else if (cat.key === 'secondary') qty = rent.counts.secondary || 0;
              else if (cat.key === 'acquired') qty = rent.counts.acquired || 0;
              else if (cat.key === 'office') qty = rent.officeUnlocked ? 1 : 0;
              const subtotal = rent.byCategory[cat.key] || 0;
              const Icon = cat.icon;
              return (
                <tr key={cat.key} className="border-t border-ink-700" data-testid={`lease-row-${cat.key}`}>
                  <td className="py-1.5 pr-3 text-slate-300 flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-slate-500" />
                    {cat.label}
                  </td>
                  <td className="py-1.5 px-2 text-center text-slate-400 font-mono">{qty}</td>
                  <td className="py-1.5 px-2 text-right text-slate-400 font-mono">{formatMoney(cat.rate)}/mois</td>
                  <td className="py-1.5 pl-2 text-right text-slate-200 font-mono font-bold">{formatMoney(subtotal)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-ink-600 font-bold">
              <td className="py-2 pr-3 text-slate-200">Total loyer brut</td>
              <td colSpan={2} />
              <td className="py-2 pl-2 text-right text-slate-200 font-mono">{formatMoney(rent.gross)}</td>
            </tr>
            {rent.aidsApplied.length > 0 && (
              <>
                {rent.aidsApplied.map(a => (
                  <tr key={a.id} className="text-emerald-400">
                    <td className="py-1 pr-3 italic text-xs flex items-center gap-1">
                      <Gift className="w-3 h-3" /> Aide active — {a.id === 'aid-hq-rent' ? 'Loyer HQ offert' : 'Loyer bureau offert'}
                    </td>
                    <td colSpan={2} />
                    <td className="py-1 pl-2 text-right font-mono">−{formatMoney(a.saved)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-cyan-700/30 font-bold text-amber-300">
                  <td className="py-2 pr-3">Net après aides</td>
                  <td colSpan={2} />
                  <td className="py-2 pl-2 text-right font-mono">{formatMoney(rent.net)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        {totalSaved > 0 && (
          <p className="mt-2 text-[10px] text-emerald-300 italic">
            💚 Économie liée aux aides régionales actives : <strong>{formatMoney(totalSaved)}/mois</strong> (≈ {formatMoney(totalSaved * 12)}/an).
          </p>
        )}
      </div>

      {/* Aides actives */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="active-aids-block">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-emerald-400" />
          <p className="text-sm font-semibold text-slate-200">Aides régionales actives ({activeAids.length})</p>
        </div>
        {activeAids.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Aucune aide active actuellement. Voir les conditions d&apos;éligibilité ci-dessous.</p>
        ) : (
          <div className="space-y-2">
            {activeAids.map(a => {
              const def = preview.find(p => p.aid.id === a.id)?.aid;
              const remainingDays = Math.max(0, a.activeUntilDay - totalDays);
              return (
                <div
                  key={a.id}
                  className="p-2.5 rounded bg-emerald-900/20 border border-emerald-700/40"
                  data-testid={`active-aid-${a.id}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <p className="text-xs font-semibold text-emerald-200">{def?.name || a.id}</p>
                    <span className="text-[10px] text-slate-400 font-mono">Reste {remainingDays} j</span>
                  </div>
                  {def?.description && (
                    <p className="text-[11px] text-slate-400 leading-relaxed">{def.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview prochain 1er janvier */}
      <div className="bg-ink-800 rounded-lg border border-cyan-700/30 p-4" data-testid="aids-preview-block">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-cyan-300" />
          <p className="text-sm font-semibold text-slate-200">Aides éligibles au prochain 1er janvier</p>
          <span className="text-[10px] text-slate-500 ml-auto italic">Mise à jour en temps réel</span>
        </div>
        <div className="space-y-2">
          {preview.map(p => {
            const Icon = p.eligible ? CheckCircle2 : AlertCircle;
            const colorClass = p.eligible ? 'text-emerald-300' : 'text-slate-500';
            const bgClass = p.eligible ? 'bg-emerald-900/15 border-emerald-700/30' : 'bg-ink-700/30 border-ink-600';
            return (
              <div
                key={p.aid.id}
                className={`p-2.5 rounded border ${bgClass}`}
                data-testid={`aid-preview-${p.aid.id}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${colorClass}`} />
                    <p className="text-xs font-semibold text-slate-200">{p.aid.name}</p>
                  </div>
                  <span className={`text-[10px] font-mono ${colorClass}`}>
                    {p.actual != null
                      ? `${typeof p.actual === 'number' && p.aid.condition.type === 'capex_year_min' ? p.actual.toLocaleString('fr-FR') + ' €' : p.actual} / ${typeof p.threshold === 'number' && p.aid.condition.type === 'capex_year_min' ? p.threshold.toLocaleString('fr-FR') + ' €' : p.threshold}`
                      : '— en attente du 1er bilan annuel'}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 italic">{p.label}</p>
                <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{p.aid.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
